import {
  BookOutlined,
  CompassOutlined,
  QuestionCircleOutlined,
  RightOutlined
} from "@ant-design/icons"
import { Alert, Breadcrumb, Button, Flex, Tag, Typography, theme } from "antd"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, useNavigate, useSearchParams } from "react-router-dom"

import { CardComponent } from "../../components/Card/CardComponent.tsx"
import { SearchBar } from "../../components/Input/SearchBar.tsx"
import { generateDocumentTitle } from "../../constants/document-title.ts"
import { RoutesPaths } from "../../constants/routes.ts"
import { useAppSelector } from "../../hooks/use-app-selector.ts"
import { GuideJourneyCard } from "./components/GuideJourneyCard.tsx"
import { GuideAreaCard } from "./components/learn/GuideAreaCard.tsx"
import { getNextIncompleteLesson } from "./content/continue-learning.ts"
import { type GuideSearchResult, searchGuideContent } from "./content/guide-search.ts"
import {
  getAccessibleLearningAreas,
  getAccessibleLessonsForArea
} from "./content/learning-areas.ts"
import { getLessonsForModule } from "./content/lessons.ts"
import { useGuideProgress } from "./hooks/use-guide-progress.ts"

const { Title, Text } = Typography

export const LearnEZBuildPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { token } = theme.useToken()
  const [query, setQuery] = useState("")
  const [searchParams, setSearchParams] = useSearchParams()
  const pages = useAppSelector((state) => state.auth?.permissions?.pages)
  const codes = useAppSelector((state) => state.auth?.permissions?.codes)
  const { progress, isLessonComplete } = useGuideProgress()

  useEffect(() => {
    generateDocumentTitle(t("guide.academy.landing.title"))
  }, [t])

  const areas = useMemo(() => getAccessibleLearningAreas(pages, codes), [pages, codes])
  const startHereLessons = getLessonsForModule("start-here")
  const quickStartMinutes = startHereLessons.reduce(
    (total, lesson) => total + lesson.durationMinutes,
    0
  )

  const searchResults = useMemo(
    () => searchGuideContent(query, t, pages, codes),
    [query, pages, codes, t]
  )

  // Re-derived from the *current* pages/codes on every render — a lesson
  // started before a permission change is never offered here once it's no
  // longer accessible (see `continue-learning.ts`).
  const nextLesson = useMemo(
    () => getNextIncompleteLesson(pages, codes, progress.completedLessonIds),
    [pages, codes, progress.completedLessonIds]
  )

  const showUnavailableNotice = searchParams.get("unavailable") === "1"
  const dismissUnavailableNotice = () => {
    const next = new URLSearchParams(searchParams)
    next.delete("unavailable")
    setSearchParams(next, { replace: true })
  }

  return (
    <Flex vertical gap={28} className="mx-auto max-w-[1200px] pb-10">
      <Breadcrumb
        items={[
          { title: <Link to={RoutesPaths.guide.home}>{t("guide.home.title")}</Link> },
          { title: t("guide.academy.landing.title") }
        ]}
      />

      {showUnavailableNotice && (
        <Alert
          type="info"
          showIcon
          closable
          message={t("guide.academy.landing.unavailable")}
          onClose={dismissUnavailableNotice}
        />
      )}

      <Flex vertical gap={8}>
        <Title level={1} className="!mb-0">
          {t("guide.academy.landing.title")}
        </Title>
        <Text className="text-xl" type="secondary">
          {t("guide.academy.landing.tagline")}
        </Text>
        <Text type="secondary" className="max-w-2xl text-base leading-relaxed">
          {t("guide.academy.landing.description")}
        </Text>
        <div className="mt-2 max-w-xl">
          <SearchBar
            size="large"
            placeholder={t("guide.academy.landing.searchPlaceholder")}
            onSearch={(value) => setQuery(value ?? "")}
          />
        </div>
      </Flex>

      {query.trim() ? (
        <Flex vertical gap={12}>
          <Title level={4} className="!mb-0">
            {t("guide.academy.landing.searchResults", { count: searchResults.length })}
          </Title>
          {searchResults.length === 0 ? (
            <Text type="secondary">{t("guide.academy.landing.noResults")}</Text>
          ) : (
            <Flex vertical gap={8}>
              {searchResults.map((result) => (
                <SearchResultRow key={`${result.type}-${resultId(result)}`} result={result} />
              ))}
            </Flex>
          )}
        </Flex>
      ) : (
        <>
          <Flex gap={16} wrap>
            <GuideJourneyCard
              icon={<CompassOutlined className="text-xl" />}
              title={t("guide.academy.landing.quickStart.title")}
              description={t("guide.academy.landing.quickStart.description")}
              duration={t("guide.lesson.duration", { minutes: quickStartMinutes })}
              onAction={() => navigate(RoutesPaths.guide.module("start-here"))}
            />
            <GuideJourneyCard
              icon={<BookOutlined className="text-xl" />}
              title={
                nextLesson
                  ? t("guide.academy.landing.continueLearning.title")
                  : t("guide.academy.landing.exploreAGuide.title")
              }
              description={
                nextLesson
                  ? t(nextLesson.lesson.titleKey)
                  : t("guide.academy.landing.exploreAGuide.description")
              }
              duration={
                nextLesson
                  ? t("guide.lesson.duration", { minutes: nextLesson.lesson.durationMinutes })
                  : t("guide.academy.landing.exploreAGuide.browseAll")
              }
              onAction={() =>
                nextLesson
                  ? navigate(RoutesPaths.guide.lesson(nextLesson.lesson.id))
                  : document
                      .getElementById("browse-by-area")
                      ?.scrollIntoView({ behavior: "smooth", block: "start" })
              }
            />
            <GuideJourneyCard
              icon={<QuestionCircleOutlined className="text-xl" />}
              title={t("guide.academy.landing.questionsAndAnswers.title")}
              description={t("guide.academy.landing.questionsAndAnswers.description")}
              duration={t("guide.academy.landing.questionsAndAnswers.cta")}
              onAction={() => navigate(RoutesPaths.guide.qa)}
            />
          </Flex>

          <Flex vertical gap={12} id="browse-by-area">
            <Title level={4} className="!mb-0">
              {t("guide.academy.landing.browseByArea")}
            </Title>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {areas.map((area) => {
                const lessons = getAccessibleLessonsForArea(area, pages, codes)
                const completedCount = lessons.filter((lesson) =>
                  isLessonComplete(lesson.id)
                ).length
                return (
                  <GuideAreaCard
                    key={area.id}
                    area={area}
                    lessonCount={lessons.length}
                    completedCount={completedCount}
                  />
                )
              })}
            </div>
          </Flex>

          <CardComponent style={{ background: token.colorFillQuaternary }} className="border-none">
            <Flex align="center" justify="space-between" wrap gap={16}>
              <Flex vertical gap={2}>
                <Text strong className="text-base">
                  {t("guide.academy.landing.footer.title")}
                </Text>
                <Text type="secondary">{t("guide.academy.landing.footer.description")}</Text>
              </Flex>
              <Button
                type="primary"
                size="large"
                icon={<RightOutlined />}
                iconPosition="end"
                onClick={() => navigate(RoutesPaths.guide.module("start-here"))}
              >
                {t("guide.academy.landing.footer.cta")}
              </Button>
            </Flex>
          </CardComponent>
        </>
      )}
    </Flex>
  )
}

const resultId = (result: GuideSearchResult): string =>
  result.type === "lesson" ? result.lesson.id : result.question.id

const SearchResultRow = ({ result }: { result: GuideSearchResult }) => {
  const { t } = useTranslation()

  if (result.type === "lesson") {
    const { lesson, module } = result
    return (
      <Link to={RoutesPaths.guide.lesson(lesson.id)}>
        <CardComponent hoverable className="cursor-pointer">
          <Flex justify="space-between" align="center" gap={12}>
            <Flex vertical gap={2}>
              <Flex align="center" gap={8}>
                <Tag color="blue">{t("guide.academy.landing.searchResultTypes.guide")}</Tag>
                <Text type="secondary" className="text-xs uppercase tracking-wide">
                  {t(module.titleKey)}
                </Text>
              </Flex>
              <Text strong>{t(lesson.titleKey)}</Text>
              <Text type="secondary" className="text-sm">
                {t(lesson.descriptionKey)}
              </Text>
            </Flex>
            <RightOutlined className="text-gray-300" />
          </Flex>
        </CardComponent>
      </Link>
    )
  }

  const { question, category } = result
  return (
    <Link to={RoutesPaths.guide.qa}>
      <CardComponent hoverable className="cursor-pointer">
        <Flex justify="space-between" align="center" gap={12}>
          <Flex vertical gap={2}>
            <Flex align="center" gap={8}>
              <Tag color="gold">{t("guide.academy.landing.searchResultTypes.question")}</Tag>
              <Text type="secondary" className="text-xs uppercase tracking-wide">
                {t(category.titleKey)}
              </Text>
            </Flex>
            <Text strong>{t(question.questionKey)}</Text>
            <Text type="secondary" className="text-sm">
              {t(question.answerKey)}
            </Text>
          </Flex>
          <RightOutlined className="text-gray-300" />
        </Flex>
      </CardComponent>
    </Link>
  )
}
